// Silent app updates, the way an installed app updates.
//
// A new version is never forced on screen and never announced: it is applied
// the next time the app goes to the background (the user switched away, or
// locked the phone), so the reload is not seen. While a pairing code is on
// screen the update waits, so the code cannot disappear; it applies at the
// first background moment after pairing ends.

import { useEffect, type ReactNode } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { usePairingCodeGuard } from '@/features/pairing/PairingCodeGuard';

export function UpdatePrompt(): ReactNode {
  const pairingActive = usePairingCodeGuard();

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url, registration) {
      // Periodically check for a new version so long-lived sessions notice it.
      if (!registration) return;
      setInterval(() => void registration.update(), 60 * 60 * 1000);
    },
  });

  useEffect(() => {
    if (!needRefresh || pairingActive) return undefined;
    const applyWhenHidden = (): void => {
      if (document.visibilityState === 'hidden') void updateServiceWorker(true);
    };
    document.addEventListener('visibilitychange', applyWhenHidden);
    return () => document.removeEventListener('visibilitychange', applyWhenHidden);
  }, [needRefresh, pairingActive, updateServiceWorker]);

  return null;
}
